import * as React from 'react';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';
import { createVirtualPlayer, previewVirtualPlayer } from '../../Api';
import { VirtualPlayerDraftDto, VirtualProviderPlayerDto } from '../../models/dtos/VirtualProviderCatalogDto';
import { VIRTUAL_FOOTBALL_ID, VirtualCatalogSportDto } from '../../models/dtos/VirtualSportsCatalogDto';
import { isIsoCountryCode } from '../../constants/IsoCountries';
import VirtualCountrySelect from './VirtualCountrySelect';

const EMPTY_DRAFT: VirtualPlayerDraftDto = {
    name: '',
    nameRu: '',
    description: '',
    descriptionRu: '',
    originCountry: '',
    yearOfBirth: new Date().getFullYear() - 20,
    eyeColor: '',
    skinColor: '',
    faceShape: '',
    hairStyle: '',
    strength: 0.5,
    technique: 0.5,
    intelligence: 0.5,
};

export default function VirtualPlayerCreationDialog(props: {
    open: boolean;
    sports: VirtualCatalogSportDto[];
    onClose: () => void;
    onCreated: (player: VirtualProviderPlayerDto) => void;
}) {
    const [sportId, setSportId] = React.useState<number | ''>('');
    const [draft, setDraft] = React.useState<VirtualPlayerDraftDto>(EMPTY_DRAFT);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        if (!props.open) return;
        setSportId(props.sports.some(sport => sport.id === VIRTUAL_FOOTBALL_ID) ? VIRTUAL_FOOTBALL_ID : '');
        setDraft({ ...EMPTY_DRAFT });
        setError('');
    }, [props.open, props.sports]);

    const update = <K extends keyof VirtualPlayerDraftDto>(field: K, value: VirtualPlayerDraftDto[K]) =>
        setDraft(current => ({ ...current, [field]: value }));
    const run = async (action: () => Promise<void>) => {
        setBusy(true);
        setError('');
        try { await action(); }
        catch (reason) { setError(reason instanceof Error ? reason.message : 'Request failed'); }
        finally { setBusy(false); }
    };
    const latestBirthYear = new Date().getFullYear() - 16;
    const valid = sportId !== ''
        && draft.name.trim().length >= 3
        && draft.nameRu.trim().length >= 3
        && draft.description.trim().length >= 40
        && draft.descriptionRu.trim().length >= 40
        && draft.eyeColor.trim().length >= 2
        && draft.skinColor.trim().length >= 2
        && draft.faceShape.trim().length >= 2
        && draft.hairStyle.trim().length >= 2
        && [draft.strength, draft.technique, draft.intelligence].every(value => value >= 0 && value <= 1)
        && isIsoCountryCode(draft.originCountry)
        && draft.yearOfBirth >= 1940
        && draft.yearOfBirth <= latestBirthYear;

    return <Dialog open={props.open} onClose={busy ? undefined : props.onClose} fullWidth maxWidth="md">
        <DialogTitle>Add virtual player</DialogTitle>
        <DialogContent>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField select required label="Virtual sport" value={sportId} onChange={event => setSportId(Number(event.target.value))}>
                    {props.sports.map(sport => <MenuItem key={sport.id} value={sport.id}>{sport.name}</MenuItem>)}
                </TextField>
                <TextField required label="Name (English)" value={draft.name} onChange={event => update('name', event.target.value)} />
                <TextField required label="Name (Russian)" value={draft.nameRu} onChange={event => update('nameRu', event.target.value)} />
                <TextField required multiline minRows={3} label="Biography (English)" value={draft.description} onChange={event => update('description', event.target.value)} />
                <TextField required multiline minRows={3} label="Biography (Russian)" value={draft.descriptionRu} onChange={event => update('descriptionRu', event.target.value)} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <VirtualCountrySelect value={draft.originCountry} onChange={countryCode => update('originCountry', countryCode)} disabled={busy} />
                    <TextField required type="number" label="Year of birth" value={draft.yearOfBirth} onChange={event => update('yearOfBirth', Number(event.target.value))} sx={{ flex: 1 }} />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField required label="Eye color" value={draft.eyeColor} onChange={event => update('eyeColor', event.target.value)} sx={{ flex: 1 }} />
                    <TextField required label="Skin color" value={draft.skinColor} onChange={event => update('skinColor', event.target.value)} sx={{ flex: 1 }} />
                </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField required label="Face shape" value={draft.faceShape} onChange={event => update('faceShape', event.target.value)} sx={{ flex: 1 }} />
                        <TextField required label="Hair style" value={draft.hairStyle} onChange={event => update('hairStyle', event.target.value)} sx={{ flex: 1 }} />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField required type="number" label="Strength" value={draft.strength} onChange={event => update('strength', Number(event.target.value))} inputProps={{ min: 0, max: 1, step: 0.01 }} helperText="0 to 1" sx={{ flex: 1 }} />
                        <TextField required type="number" label="Technique" value={draft.technique} onChange={event => update('technique', Number(event.target.value))} inputProps={{ min: 0, max: 1, step: 0.01 }} helperText="0 to 1" sx={{ flex: 1 }} />
                        <TextField required type="number" label="Intelligence" value={draft.intelligence} onChange={event => update('intelligence', Number(event.target.value))} inputProps={{ min: 0, max: 1, step: 0.01 }} helperText="0 to 1" sx={{ flex: 1 }} />
                    </Stack>
            </Stack>
        </DialogContent>
        <DialogActions>
            <Button onClick={props.onClose} disabled={busy}>Cancel</Button>
            <Button variant="outlined" disabled={sportId === '' || busy} onClick={() => run(async () => setDraft(await previewVirtualPlayer(sportId as number)))}>
                {busy ? <CircularProgress size={20} /> : 'Prepopulate with AI'}
            </Button>
            <Button variant="contained" disabled={!valid || busy} onClick={() => run(async () => {
                const created = await createVirtualPlayer(sportId as number, draft);
                props.onCreated(created);
                props.onClose();
            })}>Save</Button>
        </DialogActions>
    </Dialog>;
}
